// Professional Trading Header with Account Info
import React, { useState, useEffect, useMemo } from 'react';
import { COLORS } from '../../utils/constants';
import { TrendingUp, TrendingDown, Plus, Wallet, PieChart, Activity, DollarSign } from 'lucide-react';
import MarketSelector from './MarketSelector';
import { userAPI, tradingAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';

const TradingHeader = ({ 
  selectedSymbol, 
  onSelectSymbol, 
  currentPrice, 
  ticker, 
  wsConnected,
  onOpenTradeModal,
  tradeMode,
  setTradeMode,
  positions = [],
  refreshTrigger = 0,
}) => {
  const { t } = useLanguage();
  const [balance, setBalance] = useState(null);
  const [spotHoldings, setSpotHoldings] = useState([]);
  const [marketPrices, setMarketPrices] = useState({});
  const priceChangePercent = ticker?.priceChangePercent || ticker?.change_24h || 0;
  const isPositive = priceChangePercent >= 0;

  useEffect(() => {
    loadBalance();
    loadSpotHoldings();
    const interval = setInterval(() => {
      loadBalance();
      loadSpotHoldings();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      loadBalance();
      loadSpotHoldings();
    }
  }, [refreshTrigger]);

  // Load market prices for all position symbols
  useEffect(() => {
    const loadPrices = async () => {
      if (positions.length === 0) return;
      const uniqueSymbols = [...new Set(positions.map(p => p.symbol))];
      const prices = { ...marketPrices };
      
      await Promise.all(uniqueSymbols.map(async (sym) => {
        try {
          const response = await tradingAPI.getPrice(sym);
          prices[sym] = response.data.price;
        } catch (error) {
          console.error(`Failed to load price for ${sym}:`, error);
        }
      }));
      
      setMarketPrices(prices);
    };
    
    loadPrices();
    const interval = setInterval(loadPrices, 3000);
    return () => clearInterval(interval);
  }, [positions]);

  const loadBalance = async () => {
    try {
      const res = await userAPI.getBalance();
      setBalance(res.data);
    } catch (err) {
      console.error('Failed to load balance:', err);
    }
  };

  const loadSpotHoldings = async () => {
    try {
      const res = await tradingAPI.getHoldings();
      setSpotHoldings(res.data || []);
    } catch (err) {
      console.error('Failed to load spot holdings:', err);
    }
  };

  // Calculate account stats with correct prices for each position
  const accountStats = useMemo(() => {
    const exchangeBalance = balance?.exchange_balance || 0;
    
    // Calculate used margin from open positions
    const usedMargin = positions.reduce((sum, p) => {
      return sum + (parseFloat(p.margin) || 0);
    }, 0);
    
    // Calculate unrealized PnL from positions using correct market prices
    const unrealizedPnL = positions.reduce((sum, p) => {
      const entryPrice = parseFloat(p.entry_price) || 0;
      const margin = parseFloat(p.margin) || 0;
      const leverage = parseFloat(p.leverage) || 1;
      
      // Use market price for this specific symbol, or fall back to entry price
      const markPrice = marketPrices[p.symbol] || (p.symbol === selectedSymbol ? currentPrice : entryPrice);
      
      if (!entryPrice || !markPrice || !margin) return sum;
      
      const positionSize = (margin * leverage) / entryPrice;
      let pnl = 0;
      
      if (p.side === 'long') {
        pnl = (markPrice - entryPrice) * positionSize;
      } else {
        pnl = (entryPrice - markPrice) * positionSize;
      }
      
      return sum + pnl;
    }, 0);
    
    // Calculate spot holdings value
    const spotValue = spotHoldings.reduce((sum, h) => {
      return sum + (parseFloat(h.current_value) || parseFloat(h.value_usdt) || 0);
    }, 0);
    
    // Total equity = exchange balance + unrealized PnL
    const totalEquity = exchangeBalance + unrealizedPnL;
    
    return {
      exchangeBalance,
      usedMargin,
      unrealizedPnL,
      spotValue,
      totalEquity,
    };
  }, [balance, positions, currentPrice, spotHoldings, marketPrices, selectedSymbol]);

  const formatMoney = (val) => {
    const absVal = Math.abs(val);
    if (val >= 0) return `$${absVal.toFixed(2)}`;
    return `-$${absVal.toFixed(2)}`;
  };

  return (
    <div style={{
      backgroundColor: COLORS.card,
      padding: '8px 16px',
      borderBottom: `1px solid ${COLORS.border}`,
      position: 'sticky',
      top: 0,
      zIndex: 100,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
      }}>
        {/* Left - Symbol & Price */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
          <MarketSelector selected={selectedSymbol} onSelect={onSelectSymbol} />
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ 
              fontSize: '20px', 
              fontWeight: '800', 
              color: COLORS.text, 
              fontFamily: 'monospace',
            }}>
              ${currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 7 })}
            </span>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              padding: '3px 6px',
              borderRadius: '5px',
              background: isPositive 
                ? 'linear-gradient(135deg, #0ecb81 0%, #0ba360 100%)' 
                : 'linear-gradient(135deg, #f6465d 0%, #cf304a 100%)',
              color: '#fff',
              fontSize: '11px',
              fontWeight: '700',
            }}>
              {isPositive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
              {isPositive ? '+' : ''}{priceChangePercent.toFixed(2)}%
            </div>
          </div>
        </div>

        {/* Center - Account Stats */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '16px',
          backgroundColor: COLORS.background,
          padding: '6px 14px',
          borderRadius: '8px',
          flex: '0 1 auto',
        }}>
          {/* Exchange Balance */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Wallet size={12} color={COLORS.primary} />
            <div>
              <div style={{ fontSize: '9px', color: COLORS.textSecondary, textTransform: 'uppercase' }}>Баланс</div>
              <div style={{ fontSize: '12px', color: COLORS.text, fontWeight: '700', fontFamily: 'monospace' }}>
                {formatMoney(accountStats.exchangeBalance)}
              </div>
            </div>
          </div>

          <div style={{ width: '1px', height: '24px', backgroundColor: COLORS.border }} />

          {/* Spot Balance */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <DollarSign size={12} color={COLORS.success} />
            <div>
              <div style={{ fontSize: '9px', color: COLORS.textSecondary, textTransform: 'uppercase' }}>{ t('spot') }</div>
              <div style={{ fontSize: '12px', color: COLORS.success, fontWeight: '700', fontFamily: 'monospace' }}>
                {formatMoney(accountStats.spotValue)}
              </div>
            </div>
          </div>

          <div style={{ width: '1px', height: '24px', backgroundColor: COLORS.border }} />

          {/* Used Margin */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <PieChart size={12} color={accountStats.usedMargin > 0 ? COLORS.primary : COLORS.textSecondary} />
            <div>
              <div style={{ fontSize: '9px', color: COLORS.textSecondary, textTransform: 'uppercase' }}>Маржа</div>
              <div style={{ fontSize: '12px', color: accountStats.usedMargin > 0 ? COLORS.primary : COLORS.textSecondary, fontWeight: '700', fontFamily: 'monospace' }}>
                {formatMoney(accountStats.usedMargin)}
              </div>
            </div>
          </div>

          <div style={{ width: '1px', height: '24px', backgroundColor: COLORS.border }} />

          {/* Unrealized PnL */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Activity size={12} color={accountStats.unrealizedPnL >= 0 ? COLORS.success : COLORS.danger} />
            <div>
              <div style={{ fontSize: '9px', color: COLORS.textSecondary, textTransform: 'uppercase' }}>PnL</div>
              <div style={{ 
                fontSize: '12px', 
                color: accountStats.unrealizedPnL >= 0 ? COLORS.success : COLORS.danger, 
                fontWeight: '700', 
                fontFamily: 'monospace' 
              }}>
                {accountStats.unrealizedPnL >= 0 ? '+' : ''}{formatMoney(accountStats.unrealizedPnL)}
              </div>
            </div>
          </div>

          <div style={{ width: '1px', height: '24px', backgroundColor: COLORS.border }} />

          {/* Total Equity */}
          <div>
            <div style={{ fontSize: '9px', color: COLORS.textSecondary, textTransform: 'uppercase' }}>Эквити</div>
            <div style={{ fontSize: '12px', color: COLORS.text, fontWeight: '700', fontFamily: 'monospace' }}>
              {formatMoney(accountStats.totalEquity)}
            </div>
          </div>
        </div>

        {/* Right - Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {/* Trade Mode */}
          <div style={{
            display: 'flex',
            backgroundColor: COLORS.background,
            borderRadius: '6px',
            padding: '2px',
          }}>
            {['futures', 'spot'].map(mode => (
              <button
                key={mode}
                onClick={() => setTradeMode(mode)}
                style={{
                  padding: '5px 10px',
                  backgroundColor: tradeMode === mode ? COLORS.primary : 'transparent',
                  color: tradeMode === mode ? '#000' : COLORS.textSecondary,
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                {mode === 'futures' ? t('futures') : t('spot')}
              </button>
            ))}
          </div>

          {/* Live Indicator */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '3px',
            padding: '4px 6px',
            borderRadius: '10px',
            backgroundColor: wsConnected ? 'rgba(14, 203, 129, 0.15)' : 'rgba(246, 70, 93, 0.15)',
            color: wsConnected ? COLORS.success : COLORS.danger,
            fontSize: '9px',
            fontWeight: '700',
          }}>
            <span style={{
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              backgroundColor: wsConnected ? COLORS.success : COLORS.danger,
              animation: wsConnected ? 'pulse 2s infinite' : 'none',
            }} />
            {wsConnected ? 'LIVE' : 'OFF'}
          </div>

          {/* Open Position Button */}
          <button
            onClick={onOpenTradeModal}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              background: 'linear-gradient(135deg, #f0b90b 0%, #d4a30a 100%)',
              color: '#000',
              border: 'none',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(240, 185, 11, 0.3)',
            }}
          >
            <Plus size={12} strokeWidth={3} />
            {t('openPosition')}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

export default TradingHeader;
